import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';

import { ControllerProps } from 'src/router';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { SupplierCostFormView } from './supplier-cost-form.view';
import { BillingService } from 'src/app/accounting/shared/billing.service';
import { QuoteEntryListDataService } from 'src/app/accounting/suppliers/cost/parts/quote-entry-list.data';
import { DataLocalService } from 'src/core/services/data-local.service';

interface Props extends ControllerProps<{}> {}

interface State {
	filters: SupplierCostFilters;
	invoiceDate?: Date;
	invoiceReferenceNumber?: string;
}

@observer
export class SupplierCostFormController extends React.Component<Props, State> {
	@resolve
	private toast: ToastNotifications;

	@resolve
	private billingService: BillingService;

	@observable
	private dataService: QuoteEntryListDataService;

	@observable
	private payloadDataSet: DataSet<SelectableInvoiceEntryPayload>;

	private filtersMap = {
		suppliersId: (value: number[]): string => `(${value.map(m => `it.supplierId == ${m}`).join(' || ')})`,
		reservationDate: (value: Date): string => `it.quote.reservationDate == ${value.getTime()}`,
		quoteNumber: (value: string): string => `it.quote.quoteNumber.contains('${value}')`,
		quoteStatus: (value: number): string => `it.quote.status == ${value}`,
		serviceDateFrom: (value: Date): string => `it.dateTo >= ${value.getTime()}`,
		serviceDateTo: (value: Date): string => `it.dateFrom <= ${value.getTime()}`,
		confirmationReferenceNumber: (value: string): string => value === '' ? 'true' : `it.confirmationReferenceNumber.contains('${value}')`,
		showInvoiced: (value: boolean): string => `it.costPrice ${value ? '==' : '!='} it.invoicedCost`,
		showConfirmedOnly: (value: boolean): string => `it.status ${value ? "== 3" : ">= 0"}`,
	};

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			filters: {
				showInvoiced: false,
				showConfirmedOnly: true,
			},
			invoiceDate: new Date(),
			invoiceReferenceNumber: ''
		};
	}

	componentDidMount(): void {
		this.dataService = QuoteEntryListDataService.make();
		this.payloadDataSet = new DataLocalService<SelectableInvoiceEntryPayload>([]);

		// Global filters
		this.dataService.addFilter('isDeleted', 'eq', false);
		this.dataService.addSorting('id', 'asc', true);
		this.dataService.pageSize = 150;
	}

	private async performAsync<T>(body: () => Promise<T>): Promise<T> {
		const { setBusy } = this.props;

		setBusy(true);
		try {
			const result = await body();
			return result;
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	}

	@autobind
	private handleChangeFilter(fieldName: keyof SupplierCostFilters, value: any = null): void {
		const { filters } = this.state;
		const newFilters = { ...filters, [fieldName]: value };

		this.setState({ filters: newFilters });
	}

	@autobind
	private async handleApplyFilters(): Promise<void> {
		this.performAsync(async () => {
			const { filters } = this.state;

			const fixedConditions = ['it.productType != 6', 'it.productType != 7']; // this conditions can't be represented with the method 'addFilter'
			const predicate = Object.getOwnPropertyNames(filters)
				.filter((fieldName) => filters[fieldName] != null && fieldName in this.filtersMap)
				.map((fieldName) => this.filtersMap[fieldName](filters[fieldName]))
				.concat(fixedConditions)
				.join(' && ');

			const items = await this.dataService.load(query => predicate ? query.filter(predicate) : query);
			const newPayloads: SelectableInvoiceEntryPayload[] = [];

			for (let i = 0; i < items.length; i++) {
				let payload = this.payloadDataSet.items.find(m => m.quoteEntryId === items[i].id);
				if (payload != null) {
					payload.cost = Math.min(items[i].accrualValue, payload.cost);
				} else {
					payload = {
						checked: false,
						quoteEntryId: items[i].id,
						cost: items[i].accrualValue,
						comments: ''
					};
				}

				newPayloads.push(payload);
			}

			this.payloadDataSet = new DataLocalService<SelectableInvoiceEntryPayload>(observable(newPayloads));
		});
	}

	@autobind
	private handleChangeInvoiceDate(invoiceDate: Date): void {
		this.setState({ invoiceDate });
	}

	@autobind
	private handleChangeInvoiceReferenceNumber(invoiceReferenceNumber: string): void {
		this.setState({ invoiceReferenceNumber });
	}

	@autobind
	private handleChangeSelection(selectedIds: string[]): void {
		this.payloadDataSet.items.forEach(m => {
			m.checked = selectedIds.findIndex(p => p === m.quoteEntryId) != -1;
		});
	}

	@autobind
	private async handleGenerateInvoice(): Promise<void> {
		this.performAsync(async () => {
			const { invoiceDate, invoiceReferenceNumber } = this.state;
			const suppliersId = this.dataService.items
				.filter(m => this.payloadDataSet.items.find(p => p.quoteEntryId === m.id).checked)
				.map(m => m.supplierId);

			if (suppliersId.length === 0 || suppliersId.some(m => m !== suppliersId[0])) {
				this.toast.showToast('Invalid Operation', "Can't make an invoice for multiples suppliers", 'error');
				return;
			}

			const [supplierId] = suppliersId;
			const payloads = this.payloadDataSet.items
				.filter(m => m.checked);

			if (payloads.some(m => m.cost <= 0)) {
				this.toast.showToast('Invalid Operation', "Can't make an invoice for multiples suppliers", 'error');
				return;
			}

			await this.billingService.registerSupplierInvoices(supplierId, invoiceDate, invoiceReferenceNumber, payloads);
			this.toast.showToast('Invoice generated!', '', 'success');

			await this.handleApplyFilters();
		});
	}

	@autobind
	private async handleAdjustSale(saleAdjustment: SalesAdjustmentProductManually): Promise<void> {
		this.performAsync(async () => {
			await this.billingService.adjustQuoteEntryPrice(saleAdjustment);
			this.toast.showToast('Accounting Adjustment done!', '', 'success');

			this.handleApplyFilters();
		});
	}

	render(): React.ReactNode {
		if (this.dataService == null || this.payloadDataSet == null) {
			return null;
		}

		const { filters, invoiceDate, invoiceReferenceNumber } = this.state;

		return (
			<SupplierCostFormView
				setBusy={this.props.setBusy}
				model={this.dataService}
				filters={filters}
				invoiceDate={invoiceDate}
				invoiceReferenceNumber={invoiceReferenceNumber}
				payloadDataSet={this.payloadDataSet}
				onChangeFilter={this.handleChangeFilter}
				onApplyFilters={this.handleApplyFilters}
				onChangeInvoiceDate={this.handleChangeInvoiceDate}
				onChangeInvoiceReferenceNumber={this.handleChangeInvoiceReferenceNumber}
				onChangeSelection={this.handleChangeSelection}
				onGenerateInvoice={this.handleGenerateInvoice}
				onAdjustSale={this.handleAdjustSale}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/suppliers/cost/supplier-cost-form.controller.tsx